import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PriorityBadge, GateBadge, EvidenceBadge, ScoreBar, fmtDate, VerifiedTriggerBadge } from "@/lib/radar";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Building2, Flame, BadgeCheck, Target, Search, ShieldCheck, Zap } from "lucide-react";
import { TriggerLabel } from "@/lib/triggers";

const KPI = [
  { key: "companies_monitored", label: "Companies Monitored", icon: Building2, tone: "text-slate-900", border: "border-l-slate-400" },
  { key: "p1_immediate_review", label: "P1 Immediate Review", icon: Flame, tone: "text-red-600", border: "border-l-red-500" },
  { key: "new_verified_trigger_events", label: "New Verified Triggers", icon: Zap, tone: "text-teal-700", border: "border-l-teal-500" },
  { key: "p2_strong_prospects", label: "P2 Strong Prospects", icon: Target, tone: "text-amber-600", border: "border-l-amber-500" },
  { key: "research_required", label: "Research State", icon: Search, tone: "text-slate-600", border: "border-l-slate-300" },
  { key: "established_defence", label: "Established Defence", icon: ShieldCheck, tone: "text-slate-800", border: "border-l-slate-800" },
];

const PIE_COLORS = ["#0F766E", "#1E3A8A", "#B45309", "#0EA5E9", "#64748B", "#059669", "#7C3AED", "#DC2626", "#0891B2"];

export default function Dashboard() {
  const [data, setData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard/stats").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-slate-400 text-sm">Loading intelligence…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Intelligence Dashboard</h1>
          <p className="text-sm text-slate-500">Deterministic model <span className="num">{data.model_version}</span> · evidence-led opportunity triage</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {KPI.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.key} data-testid={`kpi-${k.key}`} className={`p-4 border-slate-200 border-l-4 ${k.border} shadow-none hover:shadow-sm transition-shadow`}>
              <div className="flex items-center justify-between">
                <Icon className={`h-4 w-4 ${k.tone}`} />
              </div>
              <div className={`num text-3xl font-semibold mt-2 ${k.tone}`}>{data[k.key]}</div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1 leading-tight">{k.label}</div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Hot leads */}
        <Card className="lg:col-span-8 p-0 border-slate-200 shadow-none" data-testid="hot-leads">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Flame className="h-4 w-4 text-red-500" />
            <h2 className="font-heading font-semibold text-slate-800">Hot Leads</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {data.hot_leads.length === 0 && <div className="p-4 text-sm text-slate-400">No P1/P2 leads.</div>}
            {data.hot_leads.map((c: any) => (
              <button key={c.id} data-testid={`hot-lead-${c.id}`} onClick={() => navigate(`/companies/${c.id}`)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">{c.company_name}</span>
                    {c.p0_trigger && <VerifiedTriggerBadge testid={`hot-trigger-${c.id}`} />}
                  </div>
                  <div className="text-xs text-slate-500 truncate"><TriggerLabel value={c.primary_trigger} testid={`dash-trigger-${c.id}`} className="cursor-help border-b border-dotted border-slate-300" /></div>
                </div>
                <div className="hidden sm:flex flex-col gap-1 w-[150px] shrink-0">
                  <div className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-wide text-slate-400 w-8">Opp</span><ScoreBar value={c.opportunity_score} /></div>
                  <div className="flex items-center gap-2"><span className="text-[10px] uppercase tracking-wide text-slate-400 w-8">Conf</span><ScoreBar value={c.evidence_confidence} kind="confidence" /></div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <PriorityBadge value={c.priority} />
                  <GateBadge value={c.action_gate} />
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Recent signals */}
        <Card className="lg:col-span-4 p-0 border-slate-200 shadow-none" data-testid="recent-signals">
          <div className="px-4 py-3 border-b border-slate-200">
            <h2 className="font-heading font-semibold text-slate-800">Recent Signals</h2>
          </div>
          <div className="divide-y divide-slate-100 max-h-[360px] overflow-auto">
            {data.recent_signals.map((s: any, i: number) => (
              <div key={i} className="px-4 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-slate-800 truncate">{s.title}</span>
                  <span className="text-[11px] text-slate-400 num whitespace-nowrap">{fmtDate(s.event_date)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-500 truncate">{s.company_name}</span>
                  <EvidenceBadge value={s.evidence_class} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Lead distribution */}
        <Card className="lg:col-span-6 p-4 border-slate-200 shadow-none" data-testid="lead-distribution">
          <h2 className="font-heading font-semibold text-slate-800 mb-2">Lead Distribution</h2>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.lead_distribution} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {data.lead_distribution.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.lead_distribution.map((d: any, i: number) => (
              <span key={i} className="text-xs text-slate-600 flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                {d.name} <span className="num text-slate-400">({d.value})</span>
              </span>
            ))}
          </div>
        </Card>

        {/* Sector distribution */}
        <Card className="lg:col-span-6 p-4 border-slate-200 shadow-none" data-testid="sector-distribution">
          <h2 className="font-heading font-semibold text-slate-800 mb-2">Sector Distribution</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.sector_distribution} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f6" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="value" fill="#0F766E" radius={[0, 3, 3, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
